import type { Dictionary } from './id'

export const en: Dictionary = {
  meta: {
    localeName: 'English',
    htmlLang: 'en',
  },
  nav: {
    home: 'Home',
    cahaya: '7 Lights',
    expedition: 'Expedition',
    science: 'Science',
    about: 'About',
    join: 'Join',
    menu: 'Menu',
    close: 'Close menu',
    open: 'Open menu',
  },
  common: {
    skipToContent: 'Skip to main content',
    learnMore: 'Learn more',
    backHome: 'Back to home',
    comingSoon: 'Coming soon',
    draftBadge: 'Draft data',
    draftHint: 'These figures are planning estimates awaiting ephemeris verification.',
    verifiedBadge: 'Verified',
    primaryBadge: 'Primary site',
    placeholder: 'Content to follow',
    switchLanguage: 'Switch language',
    basedIn: 'Based in Indonesia',
  },
  hero: {
    eyebrow: '2 August 2027 · Makkah, Saudi Arabia',
    title: 'Total Solar Eclipse 2027',
    theme: '7 Lights Expedition',
    lead: 'One event in the sky, seven paths of light. Scientific research, the Makkah expedition, and observation across the path of totality — brought together as a single programme.',
    ctaPrimary: 'Explore the 7 Lights',
    ctaSecondary: 'Join the expedition',
  },
  countdown: {
    heading: 'Counting down to totality',
    subheading: 'To second contact in Makkah (AST, UTC+3)',
    daysShort: 'Days',
    hoursShort: 'Hrs',
    minutesShort: 'Min',
    secondsShort: 'Sec',
    calendarViewLabel: 'In months and weeks',
    months: 'Months',
    weeks: 'Weeks',
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
    seconds: 'Seconds',
    statusBefore: 'Counting down to totality',
    statusPartial: 'Partial phase in progress',
    statusTotality: 'TOTALITY IN PROGRESS',
    statusAfter: 'The eclipse has ended',
    makkahTime: 'Makkah time',
    localTime: 'Your time',
  },
  timeline: {
    eyebrow: 'Timeline',
    heading: 'Eclipse timeline in Makkah',
    lead: 'The five principal contacts, shown in Makkah time and in your device time.',
    contacts: {
      partialBegin: 'Partial phase begins',
      totalityBegin: 'Totality begins',
      maximum: 'Maximum eclipse',
      totalityEnd: 'Totality ends',
      partialEnd: 'Partial phase ends',
    },
    totalityDuration: 'Totality duration',
    safetyNote:
      'Use ISO 12312-2 certified eye protection throughout every partial phase. The unaided eye is safe only during full totality.',
  },
  cahaya: {
    eyebrow: 'Programme theme',
    heading: '7 Lights Expedition',
    lead: 'Seven lights guiding every GMT 2027 activity — from the laboratory to the path of totality.',
    pillarLabel: 'Light',
    draftNotice:
      'Each light is a movement with its own deliverables. This is an initial draft — the descriptions and deliverables are still being refined by the programme team.',
    draftBadge: 'Initial draft',
    deliverables: 'Deliverables',
    pillarLeadLabel: 'Pillar lead',
    leadPerson: 'Lead',
    leadOrganization: 'Supporting organisation',
    toBeAnnounced: 'To be announced',
  },
  expedition: {
    eyebrow: 'Expedition',
    heading: 'Along the path of totality',
    lead: 'Makkah is the primary station. Several further points along the path of totality are being prepared as supporting observation sites.',
    sitesHeading: 'Candidate observation sites',
    duration: 'Totality duration',
    sunAltitude: 'Sun altitude',
    coordinates: 'Coordinates',
    note: 'The site list is still open. Final feasibility depends on permits, climatological cloud cover, and logistics.',
  },
  science: {
    eyebrow: 'Science',
    heading: 'Experiments and data collection',
    lead: 'A total eclipse is a natural laboratory that opens for five minutes only. Every experiment is designed to be reproducible by schools, communities, and researchers alike.',
    protocolHeading: 'Protocol',
    equipment: 'Equipment',
    difficulty: 'Difficulty',
    difficultyLevels: {
      school: 'School',
      community: 'Community',
      research: 'Research',
    },
    placeholderNotice:
      'The experiment list is still an outline. Full protocols will be published alongside the field guide.',
  },
  about: {
    eyebrow: 'About',
    heading: 'About GMT 2027',
    lead: 'GMT 2027 is a public programme marking the total solar eclipse of 2 August 2027 under the theme 7 Lights Expedition.',
    missionHeading: 'What we do',
    contactHeading: 'Contact',
    placeholderNotice: 'Organisation profile, team, and partners will be completed later.',
  },
  join: {
    eyebrow: 'Join',
    heading: 'Take part in GMT 2027',
    lead: 'Register your interest as an expedition participant, observation volunteer, or programme partner.',
    newsletterHeading: 'Stay informed',
    newsletterLead:
      'Receive schedule updates, experiment protocols, and news when registration opens.',
  },
  form: {
    name: 'Full name',
    email: 'Email address',
    organization: 'Institution / community',
    role: 'Role of interest',
    roles: {
      participant: 'Expedition participant',
      volunteer: 'Observation volunteer',
      researcher: 'Researcher',
      partner: 'Partner / sponsor',
      media: 'Media',
    },
    site: 'Preferred observation site',
    message: 'Additional notes',
    optional: 'optional',
    submit: 'Submit registration',
    submitting: 'Sending…',
    subscribe: 'Subscribe',
    successTitle: 'Thank you',
    successBody: 'Your details are saved. Our team will be in touch by email.',
    errorGeneric: 'Submission failed. Please try again in a moment.',
    errorOffline: 'Online registration opens soon. In the meantime, please contact us by email:',
    requiredField: 'Required',
    invalidEmail: 'Please enter a valid email address',
  },
  footer: {
    tagline: '7 Lights Expedition',
    safety: 'Always use ISO 12312-2 certified eye protection during the partial phases.',
    navHeading: 'Navigation',
    contactHeading: 'Contact',
    rights: 'All rights reserved.',
    dataNote: 'Makkah contact times are given in AST (UTC+3).',
  },
  notFound: {
    title: 'Page not found',
    lead: 'The link you followed may have moved or may not exist yet.',
  },
}
